import { useRef, useState } from 'react';
import { LuLockKeyhole, LuShieldCheck } from 'react-icons/lu';
import { useAdminToast } from '../components/AdminToastProvider';
import '../styles/admin-login.css';

function AdminLoginPage({ onAuthenticate }) {
  const notify = useAdminToast();
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const invalidToastPending = useRef(false);

  const isRegistering = mode === 'register';
  const showValidationToast = event => {
    event.preventDefault();
    if (invalidToastPending.current) return;
    invalidToastPending.current = true;
    notify(event.target.validationMessage || 'Please check the required fields.', 'error');
    window.setTimeout(() => { invalidToastPending.current = false; }, 0);
  };

  const submitAuth = async event => {
    event.preventDefault();
    const normalizedName = name.trim();
    const normalizedEmail = email.trim();
    if (isRegistering && (normalizedName.length < 2 || normalizedName.length > 100)) {
      notify('Name must be between 2 and 100 characters.', 'error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail) || normalizedEmail.length > 254) {
      notify('Enter a valid email address of 254 characters or less.', 'error');
      return;
    }
    if (password.length < 8 || new TextEncoder().encode(password).length > 72) {
      notify('Password must be at least 8 characters and no more than 72 UTF-8 bytes.', 'error');
      return;
    }
    if (isRegistering && password !== confirmPassword) {
      notify('Passwords do not match.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await onAuthenticate(mode, isRegistering
        ? { name: normalizedName, email: normalizedEmail, password }
        : { email: normalizedEmail, password });
    } catch (error) {
      notify(error.message || 'Unable to sign in. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="admin-login-page">
      <section className="admin-login-card" aria-labelledby="admin-login-title">
        <div className="admin-login-brand">
          <img src={`${process.env.PUBLIC_URL}/logow.png`} alt="AeroConveyor Logo" />
        </div>
        <span className="admin-login-icon"><LuLockKeyhole size={22} /></span>
        <span className="admin-login-eyebrow"><LuShieldCheck size={14} /> ADMINISTRATOR ACCESS</span>
        <h1 id="admin-login-title">{isRegistering ? 'Create admin account' : 'Welcome back'}</h1>
        <p className="admin-login-description">{isRegistering ? 'Register for access to airport operations and baggage systems.' : 'Sign in to manage airport operations and baggage systems.'}</p>

        <form className="admin-login-form" onSubmit={submitAuth} onInvalid={showValidationToast}>
          {isRegistering && <label>
            <span>Full name</span>
            <input
              type="text"
              autoComplete="name"
              minLength="2"
              maxLength="100"
              required
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="Your full name"
            />
          </label>}
          <label>
            <span>Email address</span>
            <input
              type="email"
              autoComplete="username"
              maxLength="254"
              required
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder="admin@example.com"
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength="8"
              maxLength="72"
              value={password}
              onChange={event => setPassword(event.target.value)}
              placeholder="Enter your password"
            />
          </label>
          {isRegistering && <label>
            <span>Confirm password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={event => setConfirmPassword(event.target.value)}
              placeholder="Re-enter your password"
            />
          </label>}
          <button type="submit" disabled={submitting}>{submitting ? 'Please wait…' : isRegistering ? 'Create account' : 'Sign in to admin'}</button>
        </form>
        <p className="admin-login-toggle">
          {isRegistering ? 'Already have an account?' : 'New administrator?'}
          <button type="button" onClick={() => setMode(isRegistering ? 'login' : 'register')}> {isRegistering ? 'Sign in' : 'Create account'}</button>
        </p>
        <p className="admin-login-notice">Public registration is enabled. Every registered account has administrator access.</p>
      </section>
    </main>
  );
}

export default AdminLoginPage;
